/**
 * Round DMS-UAT-1 (2026) — Issue 7.
 *
 * Legacy DMSNew stored Product Info as six parallel MULTILINE text columns
 * on DmsEcr (partDescription, partNumber, drawingNumber, customerPartNumber,
 * currentRev, newRev): line N of each column belongs to the same part line.
 * The modern UI rendered each column as one lump, which UAT flagged as
 * unreadable and unordered.
 *
 * These helpers parse the stored strings into ordered row objects for
 * display/editing, and serialize rows back into the SAME six columns
 * (newline-joined) so no schema change is needed and legacy data keeps
 * round-tripping safely. A ';' is treated as a secondary line delimiter on
 * PARSE only (seen in some migrated rows) but is never written back.
 */

export const PRODUCT_INFO_FIELDS = [
    { key: "partDescription",    label: "Part Description / Name" },
    { key: "partNumber",         label: "Part Number" },
    { key: "drawingNumber",      label: "Drawing Number" },
    { key: "customerPartNumber", label: "Customer Part Number" },
    { key: "currentRev",         label: "Current Rev" },
    { key: "newRev",             label: "New Rev" },
];

const normalise = (value) =>
    String(value ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");

// Legacy free-text interpretation (pre-round behaviour): blank lines are
// skipped and ';' is a secondary delimiter seen in some migrated rows.
function splitLinesLegacy(value) {
    if (value === null || value === undefined) return [];
    return normalise(value)
        .replace(/;/g, "\n")
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}

// Grid interpretation (data written by serializeProductInfoRows): line N of
// every column IS row N, blank lines included — a blank cell must stay a
// blank cell, never shift the rows below it up.
function splitLinesGrid(value) {
    return normalise(value).split("\n").map((s) => s.trim());
}

/**
 * Round DMS-IssueRound-1 (2026) — Issue 3. Decide which interpretation a
 * stored record needs. serializeProductInfoRows always writes EXACTLY N lines
 * into every one of the six columns (empty cells become empty lines), so a
 * grid-written record has identical raw line counts in all six columns.
 * Legacy free-text rows practically never do; and a single-line record that
 * uses ';' as its delimiter is legacy by definition. Anything not recognised
 * as grid-written keeps the pre-round legacy parsing exactly as it was.
 * The same rule is mirrored server-side for the PDF (DmsPdfService.BuildPartsRows).
 */
export function isGridSerialized(productInfo) {
    const raws = PRODUCT_INFO_FIELDS.map((f) => normalise(productInfo?.[f.key]));
    const counts = raws.map((r) => r.split("\n").length);
    const allEqual = counts.every((c) => c === counts[0]);
    if (!allEqual) return false;
    if (counts[0] === 1 && raws.some((r) => r.includes(";"))) return false;
    return true;
}

/**
 * parseProductInfoRows({ partDescription, partNumber, ... }) →
 *   [{ partDescription, partNumber, drawingNumber, customerPartNumber,
 *      currentRev, newRev }, ...]
 * Rows are aligned by line index and preserve the original entry order.
 */
export function parseProductInfoRows(productInfo) {
    const grid = isGridSerialized(productInfo);
    const cols = PRODUCT_INFO_FIELDS.map((f) =>
        grid ? splitLinesGrid(productInfo?.[f.key]) : splitLinesLegacy(productInfo?.[f.key]));
    const rowCount = Math.max(0, ...cols.map((c) => c.length));
    const rows = [];
    for (let i = 0; i < rowCount; i++) {
        const row = {};
        PRODUCT_INFO_FIELDS.forEach((f, colIdx) => {
            row[f.key] = cols[colIdx][i] || "";
        });
        // A fully blank line across all six columns carries no product — drop it.
        if (PRODUCT_INFO_FIELDS.some((f) => row[f.key].length > 0)) rows.push(row);
    }
    return rows;
}

/**
 * serializeProductInfoRows(rows) → { partDescription, partNumber, ... }
 * Joins each column back with "\n". Fully-empty rows are dropped. A newline
 * inside a cell value would be read back as a row boundary, so it is
 * flattened to a space (Round DMS-IssueRound-1 — pasted Excel cells can
 * contain Alt+Enter line breaks).
 */
export function serializeProductInfoRows(rows) {
    const kept = (rows || []).filter((r) =>
        PRODUCT_INFO_FIELDS.some((f) => (r[f.key] || "").trim().length > 0)
    );
    const out = {};
    PRODUCT_INFO_FIELDS.forEach((f) => {
        out[f.key] = kept
            .map((r) => String(r[f.key] || "").replace(/[\r\n]+/g, " ").trim())
            .join("\n");
    });
    return out;
}

/** A blank product line (all six columns empty). */
export function blankProductInfoRow() {
    const row = {};
    PRODUCT_INFO_FIELDS.forEach((f) => { row[f.key] = ""; });
    return row;
}

/**
 * Round DMS-IssueRound-1 (2026) — Issue 3: apply a pasted cell matrix
 * (see utils/clipboardTable.js) to the grid, starting at the focused cell
 * (rowIdx, colIdx). Rows are created as needed, cells outside the pasted
 * range are untouched, and values are never shifted into other columns —
 * a pasted column that would land beyond "New Rev" is dropped and counted.
 *
 * Returns { rows, applied, droppedCells, createdRows }.
 */
export function applyPastedMatrix(currentRows, matrix, rowIdx, colIdx) {
    const rows = (currentRows || []).map((r) => ({ ...r }));
    const keys = PRODUCT_INFO_FIELDS.map((f) => f.key);
    const startRow = Math.max(0, rowIdx || 0);
    const startCol = Math.min(Math.max(0, colIdx || 0), keys.length - 1);
    let applied = 0;
    let droppedCells = 0;
    let createdRows = 0;

    (matrix?.rows || []).forEach((row, r) => {
        const target = startRow + r;
        while (rows.length <= target) { rows.push(blankProductInfoRow()); createdRows++; }
        row.cells.forEach((raw, c) => {
            const key = keys[startCol + c];
            const value = String(raw ?? "").replace(/[\r\n]+/g, " ").trim();
            if (!key) { if (value) droppedCells++; return; }
            rows[target][key] = value;
            applied++;
        });
    });
    return { rows, applied, droppedCells, createdRows };
}

export default parseProductInfoRows;
